import { useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, Clock, Eye } from "lucide-react";
import { format } from "date-fns";
import { 
  useOutageNotifications, 
  useUnseenOutageCount,
  useMarkAllOutagesAsViewed 
} from "@/hooks/use-outage-notifications";

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case "critical":
      return "bg-destructive text-destructive-foreground";
    case "high":
      return "bg-orange-500 text-white";
    case "medium":
      return "bg-yellow-500 text-black";
    case "low":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "in_progress":
      return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    case "resolved":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "scheduled":
      return <Clock className="h-4 w-4 text-blue-500" />;
    case "cancelled":
      return <CheckCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return <AlertTriangle className="h-4 w-4" />;
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "in_progress":
      return "In Progress";
    case "resolved":
      return "Resolved";
    case "scheduled":
      return "Scheduled";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
};

export const OutageNotificationsSection = () => {
  const { data: outages, isLoading } = useOutageNotifications();
  const { data: unseenCount = 0 } = useUnseenOutageCount();
  const markAllAsSeen = useMarkAllOutagesAsViewed();

  // Calculate active count (only scheduled or in_progress)
  const activeOutages = outages?.filter(o => 
    o.status === 'scheduled' || o.status === 'in_progress'
  ) || [];

  // Auto-mark all active outages as viewed after 2 seconds
  useEffect(() => {
    if (activeOutages.length > 0 && unseenCount > 0) {
      const timer = setTimeout(() => {
        markAllAsSeen.mutate();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [activeOutages.length, unseenCount]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!outages || outages.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No active outages or maintenance</p>
        <p className="text-sm mt-2">All systems are operational</p>
      </div>
    );
  }

  if (activeOutages.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No active outages or maintenance</p>
        <p className="text-sm mt-2">All systems are operational</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Service Status</h3>
          <Badge variant="secondary">{activeOutages.length} active</Badge>
          {unseenCount > 0 && (
            <Badge variant="default">{unseenCount} new</Badge>
          )}
        </div>
        {unseenCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllAsSeen.mutate()}
          >
            <Eye className="h-4 w-4 mr-2" />
            Mark all as seen
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {activeOutages.map((outage) => (
          <Card 
            key={outage.id} 
            className="overflow-hidden"
            style={{
              borderLeftWidth: '4px',
              borderLeftColor: outage.severity === 'critical' ? 'hsl(var(--destructive))' :
                               outage.severity === 'high' ? '#f97316' :
                               outage.severity === 'medium' ? '#eab308' :
                               'hsl(var(--muted))'
            }}
          >
            <div className="p-4 space-y-2">
              {/* Header: Icon + Title on left, description in center, badges on right */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-shrink-0">
                  {getStatusIcon(outage.status)}
                  <h4 className="font-semibold text-base">{outage.title}</h4>
                </div>
                {outage.description && (
                  <p className="text-sm text-muted-foreground flex-1 text-center">
                    {outage.description}
                  </p>
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={getSeverityColor(outage.severity)}>
                    {outage.severity.toUpperCase()}
                  </Badge>
                  <Badge variant="outline">
                    {getStatusLabel(outage.status)}
                  </Badge>
                </div>
              </div>
              
              {/* Affected Services - inline */}
              {outage.affected_services && outage.affected_services.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">Affected:</span>
                  {outage.affected_services.map((service, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {service}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Schedule - single line */}
              {(outage.scheduled_start || outage.scheduled_end) && (
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                  {outage.scheduled_start && (
                    <>
                      <span className="font-medium">Start:</span>
                      <span>{format(new Date(outage.scheduled_start), "PPp")}</span>
                    </>
                  )}
                  {outage.scheduled_start && outage.scheduled_end && (
                    <span>•</span>
                  )}
                  {outage.scheduled_end && (
                    <>
                      <span className="font-medium">End:</span>
                      <span>{format(new Date(outage.scheduled_end), "PPp")}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
